import { createLogger } from './logger';
import { QUERY_BUILDER_VERSION } from './PlaceQueryBuilder';

const log = createLogger('GeoCache');

const STORAGE_KEY = `wt_geocache_v${QUERY_BUILDER_VERSION}`;
const FAIL_KEY = `wt_geofail_v${QUERY_BUILDER_VERSION}`;
const VERSIONED_KEY_RE = /^wt_geo(?:cache|fail)_v\d+$/;
const DEFAULT_MAX_ENTRIES = 1000;

export interface GeoCacheEntry {
  lat: number;
  lng: number;
  resolvedAt: string; // ISO-8601 date-time string
  /** True when only a broader area (not the locality itself) was found. */
  approximate?: boolean;
}

/** Key of the entry with the smallest date, or undefined for an empty record. */
function oldestKey<T>(record: Record<string, T>, dateOf: (v: T) => string): string | undefined {
  let oldest: string | undefined;
  let oldestDate = '';
  for (const [k, v] of Object.entries(record)) {
    const d = dateOf(v);
    if (oldest === undefined || d < oldestDate) {
      oldest = k;
      oldestDate = d;
    }
  }
  return oldest;
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const rebuilt: Record<string, T> = {};
  for (const [k, v] of Object.entries(record)) {
    if (k !== key) {
      rebuilt[k] = v;
    }
  }
  return rebuilt;
}

/**
 * localStorage-backed geocode cache keyed by normalized place name.
 *
 * Persists between sessions so repeat GEDCOM loads skip Nominatim entirely.
 * Degrades to in-memory-only when localStorage is unavailable (Safari private
 * mode, some corporate environments).
 *
 * Evicts the oldest entry (by resolvedAt) when maxEntries is reached.
 *
 * Also keeps a failure cache (places no query variant could resolve). Both caches
 * are keyed by QUERY_BUILDER_VERSION, so improved query rules retry old failures
 * and re-check old results; keys from other versions are removed on construction.
 */
export class GeoCache {
  private readonly maxEntries: number;
  private cache: Record<string, GeoCacheEntry>;
  private failures: Record<string, string>; // key → ISO failedAt

  constructor(maxEntries: number = DEFAULT_MAX_ENTRIES) {
    this.maxEntries = maxEntries;
    this._removeStaleKeys();
    this.cache = this._load<GeoCacheEntry>(STORAGE_KEY);
    this.failures = this._load<string>(FAIL_KEY);
  }

  get(normalizedPlace: string): GeoCacheEntry | null {
    return this.cache[normalizedPlace] ?? null;
  }

  set(normalizedPlace: string, entry: GeoCacheEntry): void {
    if (this.size() >= this.maxEntries) {
      this._evictOldest();
    }
    this.cache[normalizedPlace] = entry;
    this._persist(STORAGE_KEY, this.cache);
  }

  size(): number {
    return Object.keys(this.cache).length;
  }

  /** True when this place already failed under the current query rules. */
  getFailure(normalizedPlace: string): boolean {
    return normalizedPlace in this.failures;
  }

  setFailure(normalizedPlace: string): void {
    if (
      !(normalizedPlace in this.failures) &&
      Object.keys(this.failures).length >= this.maxEntries
    ) {
      const oldest = oldestKey(this.failures, (v) => v);
      if (oldest !== undefined) {
        this.failures = withoutKey(this.failures, oldest);
      }
    }
    this.failures[normalizedPlace] = new Date().toISOString();
    this._persist(FAIL_KEY, this.failures);
  }

  private _load<T>(key: string): Record<string, T> {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        return {};
      }
      return JSON.parse(raw) as Record<string, T>;
    } catch {
      log.warn(`GeoCache: failed to load ${key} from localStorage, starting empty`);
      return {};
    }
  }

  private _persist(key: string, value: object): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      log.warn(`GeoCache: failed to persist ${key} to localStorage (storage full or blocked)`);
    }
  }

  /** Entries from other query-builder versions are never read again — drop them. */
  private _removeStaleKeys(): void {
    try {
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k !== null && VERSIONED_KEY_RE.test(k) && k !== STORAGE_KEY && k !== FAIL_KEY) {
          stale.push(k);
        }
      }
      for (const k of stale) {
        localStorage.removeItem(k);
      }
      if (stale.length > 0) {
        log.info(`GeoCache: removed ${stale.length.toString()} stale cache key(s)`);
      }
    } catch {
      log.warn('GeoCache: could not scan localStorage for stale cache keys');
    }
  }

  private _evictOldest(): void {
    const oldest = oldestKey(this.cache, (v) => v.resolvedAt);
    if (oldest !== undefined) {
      this.cache = withoutKey(this.cache, oldest);
    }
  }
}
